/** Small, pure string helpers used across the domain layer. */

/** Converts unknown cell content to trimmed text (`null`/`undefined` → ""). */
export function toText(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

/** Lowercases and strips diacritics, e.g. "Høyt" → "hoyt". */
export function stripDiacritics(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

/** Slug used for CSS class fragments and generated keys. */
export function slugify(value: unknown): string {
  return toText(value)
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/** Case-, accent- and whitespace-insensitive comparison key. */
export function toMatchKey(value: unknown): string {
  return stripDiacritics(toText(value)).toLowerCase().replace(/\s+/g, " ").trim();
}

/** Parses a number that may use a decimal comma; returns `null` when invalid. */
export function toNumberOrNull(value: unknown): number | null {
  const text = toText(value);
  if (!text) return null;
  const number = Number(text.replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

/** Natural ("1, 2, 10") locale comparison. */
export function compareNatural(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

/** Splits a multi-value cell on newlines, semicolons and commas. */
export function splitMultiValue(value: unknown): string[] {
  if (value == null) return [];
  return String(value)
    .split(/[\n;,]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Returns true for keys that would be unsafe as plain-object property names. */
export function isUnsafeObjectKey(key: string): boolean {
  return key === "prototype" || Object.prototype.hasOwnProperty.call(Object.prototype, key);
}
